import type { LooseRelationship } from '../../../../../types/electron';
import type { Column } from '../../../../sql-resolver/types/schema';
import { matchColumnName } from '../../../common/utils/columnNameMatch';
import type { TableExplorerColumn } from '../../../core/types/workspaceTypes';

/**
 * The owning table's MetaInfo coordinates plus its live loose-FK
 * declarations (`DatabaseMetaInfo.looseRelationships`, straight from
 * `useMetaInfo` — never the schema snapshot's pull-time copy).
 */
export type LooseFkContext = {
  readonly schema: string;
  readonly table: string;
  readonly looseRelationships: readonly LooseRelationship[] | undefined;
  /**
   * The owning table's column names, when known: a declaration spelled
   * another way applies to the one column it names ignoring case, never to
   * one of two case variants (`matchColumnName`).
   */
  readonly columnNames?: readonly string[];
};

/**
 * The first loose FK declared on `(schema, table, column)` — the same match
 * Table Explorer's `buildForeignKeyMap` applies (exact schema + table; the
 * declaration's column read as the table column it names — its exact name,
 * else the one column equal to it ignoring case — first declaration wins).
 * Never `normalizeColumnName`: a declaration on `"ref-id"` is not one on
 * `"ref id"`.
 */
function findLooseFk(ctx: LooseFkContext, columnName: string): LooseRelationship | undefined {
  const names = ctx.columnNames && ctx.columnNames.length > 0 ? ctx.columnNames : [columnName];
  return ctx.looseRelationships?.find(
    (r) =>
      r.fromSchema === ctx.schema &&
      r.fromTable === ctx.table &&
      matchColumnName(r.fromColumn, names) === columnName,
  );
}

/**
 * Map a resolver schema `Column` to the `TableExplorerColumn` model consumed
 * by `DraftFilterChip` / `FilterWizard`.
 *
 * ## `foreignKeyRef` / `foreignKeyRefTable` semantics (PARITY-FK-01)
 * `Column.foreignKeyRef` is a `QualifiedName` where `name` is the **referenced
 * column** name. Evidence: `plainJoin.ts` matches `foreignKeyRef.foldedName`
 * against `rightColumn.qualified.foldedName` (column names). `QualifiedName`
 * has `database?`, `schema?`, and `name` (leaf) but no separate `table` field,
 * so a bare column QualifiedName carries no schema of its own — the schema
 * belongs to the TABLE, not the column.
 *
 * `Column.foreignKeyRefTable` is a `QualifiedName` for the **referenced
 * table** (reuses `FkEdge.toTable`, so it DOES carry `schema`). Both fields
 * are populated by `columnsFor()` (`schemaProjectionHelpers.ts`) from the
 * per-engine `FkEdge[]` each projector already computes for real FK
 * constraints — additive/optional, so columns projected without an `fks`
 * thread (or non-FK columns) leave both undefined, and this mapper degrades
 * gracefully (no `referenced*` fields emitted).
 *
 * `referencedSchema` sources from `foreignKeyRefTable.schema` (the TABLE's
 * schema) — NOT `foreignKeyRef.schema`, which a bare column QualifiedName
 * never carries. The loose-FK lookup in the chip resolves its own targets
 * from MetaInfo (connection + database) independently of these fields; the
 * enum picker uses (schema, table, column) sourced separately. The other
 * fields the chip reads are `name`, `dataType`, `nullable`, `isPrimaryKey`,
 * `isForeignKey`, `isNativeEnumType?`, `isLoose?`, and `jsonClassification?`.
 *
 * ## Loose FKs (`looseFk`, M2 — Table Explorer parity)
 * Table Explorer's `useTableMetadata` merges MetaInfo loose FKs into its
 * columns (`isForeignKey` + `isLoose` + `referenced*`; a single-column real
 * FK wins, a composite-FK member yields — `realFkOutranksLoose`). The
 * chip's operator order (`lookup` first for an FK) and its target-filter
 * resolution both read those fields, so the VQE adapter applies the SAME
 * merge when given the owning table's live declarations — without it a
 * loose-FK column defaulted to `=` and lost its declaration's `targetFilter`.
 */
export function toTableExplorerColumn(col: Column, looseFk?: LooseFkContext): TableExplorerColumn {
  const ref = col.foreignKeyRef;
  const refTable = col.foreignKeyRefTable;
  const base: TableExplorerColumn = {
    name: col.qualified.name,
    // Table Explorer's spelling (PG `format_type`: `integer[]`, not `_int4`).
    dataType: col.formattedType ?? col.dataType,
    nullable: col.nullable,
    isPrimaryKey: col.isPrimaryKey,
    isForeignKey: col.isForeignKey,
    ...(refTable?.schema ? { referencedSchema: refTable.schema } : {}),
    ...(refTable ? { referencedTable: refTable.name } : {}),
    ...(ref ? { referencedColumn: ref.name } : {}),
    ...(col.jsonClassification ? { jsonClassification: col.jsonClassification } : {}),
    ...(col.isNativeEnumType ? { isNativeEnumType: true } : {}),
  };
  // A real single-column FK constraint wins on conflict; a composite-FK
  // member yields to its declaration — same precedence as TE
  // (`realFkOutranksLoose`).
  if ((col.isForeignKey && !col.foreignKeyIsComposite) || !looseFk) return base;
  const loose = findLooseFk(looseFk, col.qualified.name);
  if (!loose) return base;
  return {
    ...base,
    isForeignKey: true,
    isLoose: true,
    referencedSchema: loose.toSchema,
    referencedTable: loose.toTable,
    referencedColumn: loose.toColumn,
  };
}
